// First-party, cookie-light analytics. No personal data: a random visitor id in
// localStorage plus the event name, path and (server-side) country code.
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VISITOR_KEY = "inz_vid";
const UTM_KEY = "inz_utm";

/** First-touch campaign tags, kept for the session so later events keep the source. */
const getUtm = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const fresh = {
      utm_source: params.get("utm_source") || "",
      utm_medium: params.get("utm_medium") || "",
      utm_campaign: params.get("utm_campaign") || "",
    };
    if (fresh.utm_source || fresh.utm_medium || fresh.utm_campaign) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify(fresh));
      return fresh;
    }
    const saved = sessionStorage.getItem(UTM_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch (e) {
    return {};
  }
};

export const getVisitorId = () => {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `v_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch (e) {
    return "";
  }
};

/** Fire-and-forget event. Never blocks or breaks the UI. */
export const track = (event, extra = {}) => {
  try {
    const body = JSON.stringify({
      event,
      path: window.location.pathname,
      visitor_id: getVisitorId(),
      referrer: document.referrer ? document.referrer.slice(0, 300) : "",
      ...getUtm(),
      ...extra,
    });
    if (navigator.sendBeacon) {
      const ok = navigator.sendBeacon(
        `${API}/track`,
        new Blob([body], { type: "application/json" })
      );
      if (ok) return;
    }
    fetch(`${API}/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* analytics must never break the page */
  }
};

/** Records one page_view per route change (admin pages excluded). */
export const usePageView = () => {
  const location = useLocation();
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    track("page_view", { path: location.pathname });
  }, [location.pathname]);
};
